/**
 * `<pg-empty-state>` — estado de conteúdo ausente, reutilizável por
 * qualquer seção/tela que precise comunicar "sem dado", "carregando",
 * "erro" ou "indisponível", sem cada `module` reinventar o próprio
 * bloco de mensagem (CODING_STANDARDS.md, seção 15/16).
 *
 * Atributo `variant`: 'vazio' (padrão) | 'carregando' | 'erro' |
 * 'indisponivel' | 'sem-permissao'.
 * Atributos `titulo` e `descricao`: texto simples (strings), como em
 * `pg-table` (`empty-message`) — não são estruturas complexas o
 * suficiente para justificar propriedade JS.
 *
 * Slot padrão (opcional): ação associada ao estado (ex: `<pg-button>`
 * de "Tentar novamente"). O componente nunca sabe o que a ação faz.
 *
 * Uso:
 * ```html
 * <pg-empty-state
 *   variant="indisponivel"
 *   titulo="Pedidos"
 *   descricao="Módulo ainda não implementado nesta sprint."
 * ></pg-empty-state>
 * ```
 */
export class PgEmptyState extends HTMLElement {
    static get observedAttributes() {
        return ['variant', 'titulo', 'descricao'];
    }
    root;
    constructor() {
        super();
        this.root = this.attachShadow({ mode: 'open' });
    }
    connectedCallback() {
        this.render();
    }
    attributeChangedCallback() {
        this.render();
    }
    render() {
        const variant = this.getAttribute('variant') ?? 'vazio';
        const titulo = this.getAttribute('titulo') ?? '';
        const descricao = this.getAttribute('descricao') ?? '';
        const chave = ehChaveRotulo(variant) ? variant : 'vazio';
        const rotulo = ROTULOS[chave];
        this.root.innerHTML = `
      <style>
        :host { display: block; }
        .estado {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: var(--pg-space-2, 8px);
          padding: var(--pg-space-6, 32px);
          text-align: center;
          font-family: var(--pg-font-familia-base, system-ui);
        }
        .icone {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          margin-bottom: var(--pg-space-1, 4px);
          color: ${rotulo.cor};
          opacity: 0.7;
        }
        .icone svg { width: 100%; height: 100%; }
        .icone.girando svg { animation: pg-empty-girar 0.9s linear infinite; }
        @media (prefers-reduced-motion: reduce) {
          .icone.girando svg { animation: none; }
        }
        @keyframes pg-empty-girar {
          to { transform: rotate(360deg); }
        }
        .eyebrow {
          font-size: var(--pg-font-size-xs, 0.75rem);
          font-weight: var(--pg-font-weight-semibold, 600);
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: ${rotulo.cor};
        }
        .titulo {
          font-size: var(--pg-font-size-base, 1rem);
          font-weight: var(--pg-font-weight-medio, 500);
          color: var(--pg-color-texto-primario, #f2f3f5);
        }
        .titulo:empty { display: none; }
        .descricao {
          font-size: var(--pg-font-size-sm, 0.875rem);
          color: var(--pg-color-texto-secundario, #9aa1ab);
          max-width: 40ch;
        }
        .descricao:empty { display: none; }
        .acao:empty { display: none; }
        .acao { margin-top: var(--pg-space-2, 8px); }
      </style>
      <div class="estado" part="estado">
        <span class="icone ${chave === 'carregando' ? 'girando' : ''}" aria-hidden="true">${ICONES[chave]}</span>
        <span class="eyebrow">${escaparHtml(rotulo.texto)}</span>
        <span class="titulo">${escaparHtml(titulo)}</span>
        <span class="descricao">${escaparHtml(descricao)}</span>
        <div class="acao"><slot></slot></div>
      </div>
    `;
    }
}
const ROTULOS = {
    vazio: { texto: 'Nenhum registro', cor: 'var(--pg-color-texto-secundario, #9aa1ab)' },
    carregando: { texto: 'Carregando', cor: 'var(--pg-color-destaque, #2f8cff)' },
    erro: { texto: 'Erro', cor: 'var(--pg-color-erro, #e5484d)' },
    indisponivel: { texto: 'Indisponível', cor: 'var(--pg-color-texto-secundario, #9aa1ab)' },
    'sem-permissao': { texto: 'Sem permissão', cor: 'var(--pg-color-alerta, #e0a52c)' },
};
/** Mesmo estilo visual dos ícones do resto do app (24x24, stroke-width 2, sem preenchimento — `erp-sidebar`/`pg-kpi-card`) — puramente decorativo, nunca implica dado. */
const ICONES = {
    vazio: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></svg>',
    carregando: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M21 12a9 9 0 1 1-9-9" /></svg>',
    erro: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>',
    indisponivel: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></svg>',
    'sem-permissao': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>',
};
function ehChaveRotulo(valor) {
    return Object.prototype.hasOwnProperty.call(ROTULOS, valor);
}
function escaparHtml(valor) {
    const div = document.createElement('div');
    div.textContent = valor;
    return div.innerHTML;
}
customElements.define('pg-empty-state', PgEmptyState);
//# sourceMappingURL=pg-empty-state.js.map